/**
 * Map Store - Mapbox viewport & layer state
 */

import { create } from 'zustand';
import type {
  MapViewport,
  MapLayerVisibility,
  AirQualityGeoJSON,
  RouteGeoJSON,
  ThreatZoneGeoJSON,
} from '@/types';
import { DEFAULT_VIEWPORT } from '@/types';

interface MapState {
  // Viewport
  viewport: MapViewport;
  isMapLoaded: boolean;
  isMapInteracting: boolean;

  // Layer visibility
  layerVisibility: MapLayerVisibility;

  // Data sources
  plumeData: {
    pm25: AirQualityGeoJSON | null;
    ozone: AirQualityGeoJSON | null;
    co2: AirQualityGeoJSON | null;
  };
  activeRoute: RouteGeoJSON | null;
  threatZones: ThreatZoneGeoJSON[];

  // Map style
  currentStyle: string;

  // Actions
  setViewport: (viewport: Partial<MapViewport>) => void;
  setMapLoaded: (loaded: boolean) => void;
  setMapInteracting: (interacting: boolean) => void;
  toggleLayer: (layer: keyof MapLayerVisibility) => void;
  setLayerVisibility: (layer: keyof MapLayerVisibility, visible: boolean) => void;
  setPlumeData: (
    type: 'pm25' | 'ozone' | 'co2',
    data: AirQualityGeoJSON | null
  ) => void;
  setActiveRoute: (route: RouteGeoJSON | null) => void;
  setThreatZones: (zones: ThreatZoneGeoJSON[]) => void;
  setMapStyle: (style: string) => void;
  flyTo: (coordinates: { lat: number; lng: number }, zoom?: number) => void;
  resetViewport: () => void;
}

const defaultLayerVisibility: MapLayerVisibility = {
  plumePM25: true,
  plumeOzone: false,
  plumeCO2: false,
  safeRoute: true,
  threatZones: true,
  buildings3D: true,
  userLocation: true,
};

export const useMapStore = create<MapState>()((set, get) => ({
  viewport: DEFAULT_VIEWPORT,
  isMapLoaded: false,
  isMapInteracting: false,
  layerVisibility: defaultLayerVisibility,
  plumeData: {
    pm25: null,
    ozone: null,
    co2: null,
  },
  activeRoute: null,
  threatZones: [],
  currentStyle: 'mapbox://styles/mapbox/navigation-night-v1',

  setViewport: (newViewport) =>
    set((state) => ({
      viewport: { ...state.viewport, ...newViewport },
    })),

  setMapLoaded: (isMapLoaded) => set({ isMapLoaded }),

  setMapInteracting: (isMapInteracting) => set({ isMapInteracting }),

  toggleLayer: (layer) =>
    set((state) => ({
      layerVisibility: {
        ...state.layerVisibility,
        [layer]: !state.layerVisibility[layer],
      },
    })),

  setLayerVisibility: (layer, visible) =>
    set((state) => ({
      layerVisibility: {
        ...state.layerVisibility,
        [layer]: visible,
      },
    })),

  setPlumeData: (type, data) =>
    set((state) => ({
      plumeData: {
        ...state.plumeData,
        [type]: data,
      },
    })),

  setActiveRoute: (activeRoute) => set({ activeRoute }),

  setThreatZones: (threatZones) => set({ threatZones }),

  setMapStyle: (currentStyle) => set({ currentStyle }),

  flyTo: (coordinates, zoom) => {
    const { viewport } = get();
    set({
      viewport: {
        ...viewport,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        zoom: zoom ?? viewport.zoom,
      },
    });
  },

  resetViewport: () => set({ viewport: DEFAULT_VIEWPORT }),
}));

// Selectors
export const selectViewport = (state: MapState) => state.viewport;
export const selectLayerVisibility = (state: MapState) => state.layerVisibility;
export const selectPlumeData = (state: MapState) => state.plumeData;
export const selectActiveRoute = (state: MapState) => state.activeRoute;
